import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  ChatResponse,
  KpisByCategory,
  Message,
  ResponseState,
  Segment,
  StudyMilestones
} from '@models';
import { environment } from 'environments/environment';
import { catchError, map, Observable, of } from 'rxjs';

import { UserDataService } from '../userdata/userdata.service';

@Injectable({
  providedIn: 'root'
})
export class StudyInsightsService {
  private readonly BASE_URL = environment.CRG_DIGITALIZATION_SERVICE_URL;
  private http: HttpClient = inject(HttpClient);
  private userDataService = inject(UserDataService);

  getStudyInsights(
    milestones: StudyMilestones,
    kpis: KpisByCategory,
    agent = 'studyinsights'
  ): Observable<ResponseState<Message[]>> {
    const content = this.encodeJSON(milestones, kpis);
    const metadata = this.createMetadata();
    const payload = this.createPayload(content, metadata);
    return this.http
      .post<ChatResponse>(`${this.BASE_URL}/crg-ai-service/api/v1/agents/${agent}`, payload, {
        withCredentials: true
      })
      .pipe(
        map((data) => {
          return this.mapMessagesResponse(data);
        }),
        catchError((error) => {
          return of({
            status: 'error',
            errorCode: error.status
          } as const);
        })
      );
  }

  private mapMessagesResponse(data: ChatResponse) {
    const parsedMessages = data.messages.map((message) => {
      message.content = this.tryParseSegments(message.content);
      return message;
    });
    return {
      data: parsedMessages,
      status: 'success'
    } as const;
  }

  private tryParseSegments(value: any): Segment[] | string {
    if (typeof value !== 'string') return value as any;

    const first = value.trim()[0];
    if (first !== '{' && first !== '[') {
      return value; // definitely not JSON-like
    }

    try {
      const json = JSON.parse(value);
      return json.segments === undefined ? value : json.segments;
    } catch {
      // Not valid JSON, just return original string
      return value;
    }
  }

  private createPayload(content: string, metadata: Record<string, any>) {
    return {
      messages: [
        {
          role: 'user',
          content: content
        }
      ],
      metadata: metadata
    };
  }

  private encodeJSON(milestones: StudyMilestones, kpis: KpisByCategory) {
    return JSON.stringify({
      milestones: milestones,
      kpis: kpis
    });
  }

  private createMetadata(): Record<string, any> {
    const clientNames = this.userDataService.getStandardClientNames();
    const orgIds = this.userDataService.getUserOrgIds();
    const username = this.userDataService.getUsername();
    return {
      org_ids: orgIds,
      user_id: username,
      standard_client_names: clientNames
    };
  }
}
